import { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { Dialog, DialogContent, type DialogProps } from '@/shared/components/Dialog';
import { useIsDesktop } from '@/shared/lib/hooks';
import { getRequestContext } from '@/shared/lib/requestContext';
import styles from './PayboxModal.module.css';

type PayBoxModalProps = Omit<DialogProps, 'children'> & {
    payboxUrl: string;
};

export const PayBoxModal = (props: PayBoxModalProps) => {
    const {
        company: { companyShopUrl },
    } = getRequestContext();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const { payboxUrl, onClose, ...other } = props;
    const [payboxHeight, setPayboxHeight] = useState(0);
    const [payboxWidth, setPayboxWidth] = useState(0);
    const [paymentSuccess, setPaymentSuccess] = useState(false);

    const { isDesktop } = useIsDesktop();

    const onCloseCallback = useCallback(() => {
        onClose();

        if (paymentSuccess && companyShopUrl) {
            window.location.href = companyShopUrl;
        }
    }, [companyShopUrl, onClose, paymentSuccess]);

    const messageHandler = useCallback(
        (message: MessageEvent) => {
            if (message.source !== iframeRef.current?.contentWindow) {
                return;
            }

            const payboxEventData = JSON.parse(message.data);

            if (payboxEventData.type === 'form-success') {
                setPaymentSuccess(true);
            }

            if (payboxEventData.type === 'form-resize') {
                const payboxEventDataValue = JSON.parse(payboxEventData.value);

                setPayboxHeight(payboxEventDataValue.value.height);
                setPayboxWidth(payboxEventDataValue.value.width);
            }

            if (
                companyShopUrl &&
                (payboxEventData.type === 'form-redirect-success' || payboxEventData.type === 'form-redirect-error')
            ) {
                onClose();
                window.location.href = companyShopUrl;
            }

            if (payboxEventData.type === 'form-redirect-close') {
                onClose();
            }
        },
        [companyShopUrl, onClose]
    );

    useEffect(() => {
        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, [messageHandler]);

    return (
        <Dialog
            {...other}
            onClose={onCloseCallback}
            closable={Boolean(payboxHeight && payboxWidth)}
            isFullscreen={!isDesktop}
            containerClassName={styles.container}
            className={cn(styles.modal, {
                [styles.hidden]: !payboxHeight || !payboxWidth,
            })}
            sizes={
                isDesktop
                    ? {
                          height: payboxHeight,
                          width: payboxWidth,
                      }
                    : undefined
            }
        >
            <DialogContent className={styles.container} gap="24">
                <iframe
                    width={isDesktop ? payboxWidth : undefined}
                    height={isDesktop ? payboxHeight : undefined}
                    className={styles.container}
                    ref={iframeRef}
                    src={payboxUrl}
                />
            </DialogContent>
        </Dialog>
    );
};
